import { create } from 'zustand'

export type SocketConnectionStatus = 'idle' | 'loading' | 'opened' | 'closed' | 'error'

type PendingSocketAck = {
  resolve: () => void
  reject: (err: unknown) => void
}

interface SocketState {
  socketConnectionStatus: SocketConnectionStatus
  isAuthSuccess: boolean
  isMessageResponseWaiting: boolean
  retryAttempt: number
  isReconnecting: boolean
  pendingUploadFileAck: PendingSocketAck | null
  pendingNewTopicAck: PendingSocketAck | null
  isPreparing: boolean
}

interface SocketActions {
  setSocketConnectionStatus: (status: SocketConnectionStatus) => void
  setIsAuthSuccess: (isAuthSuccess: boolean) => void
  setIsMessageResponseWaiting: (isMessageResponseWaiting: boolean) => void
  setRetryAttempt: (retryAttempt: number | ((prev: number) => number)) => void
  setIsReconnecting: (isReconnecting: boolean) => void
  setPendingUploadFileAck: (ack: PendingSocketAck | null) => void
  setPendingNewTopicAck: (ack: PendingSocketAck | null) => void
  setIsPreparing: (isPreparing: boolean) => void
  resetSocket: () => void
}

type SocketStore = SocketState & SocketActions

const initialState: SocketState = {
  socketConnectionStatus: 'loading',
  isAuthSuccess: false,
  isMessageResponseWaiting: false,
  retryAttempt: 0,
  isReconnecting: false,
  pendingUploadFileAck: null,
  pendingNewTopicAck: null,
  isPreparing: true
}

export const useSocketStore = create<SocketStore>((set, get) => ({
  ...initialState,

  setSocketConnectionStatus: socketConnectionStatus => set({ socketConnectionStatus }),

  setIsAuthSuccess: isAuthSuccess => set({ isAuthSuccess }),

  setIsMessageResponseWaiting: isMessageResponseWaiting => set({ isMessageResponseWaiting }),

  setRetryAttempt: retryAttempt =>
    set(state => ({
      retryAttempt: typeof retryAttempt === 'function' ? retryAttempt(state.retryAttempt) : retryAttempt
    })),

  setIsReconnecting: isReconnecting => set({ isReconnecting }),

  setPendingUploadFileAck: pendingUploadFileAck => set({ pendingUploadFileAck }),

  setPendingNewTopicAck: pendingNewTopicAck => set({ pendingNewTopicAck }),

  setIsPreparing: isPreparing => set({ isPreparing }),

  resetSocket: () => set(initialState)
}))
